import { assoc, chain } from 'icepick';
import { LOCAL_STORAGE_SOURCE } from '@atlassian/jira-servicedesk-queues-common/src/model';

import {
	type ExperienceResult,
	EXPERIENCE_PENDING,
	EXPERIENCE_SUCCEEDED,
	EXPERIENCE_FAILED,
} from '../../../../common/constants';
import { LOAD_ISSUES_ACTION_SOURCE_PAGE } from '../../../../model';
import {
	type ExperienceTrackingResetAction,
	EXPERIENCE_TRACKING_RESET,
} from '../../../actions/experience-tracking';
import {
	type LoadIssuesFailureAction,
	LOAD_ISSUES_FAILURE,
	LOAD_ISSUES_SUCCESS,
	type LoadIssuesSuccessAction,
} from '../../../actions/issue';
import {
	LOAD_PAGE_DATA_FAILURE,
	RESET_STATE,
	type ResetStateAction,
	type LoadPageDataFailureAction,
} from '../../../actions/page';

export type Actions =
	| ExperienceTrackingResetAction
	| LoadIssuesFailureAction
	| LoadPageDataFailureAction
	| ResetStateAction
	| LoadIssuesSuccessAction;

export type ExperienceTrackingState = {
	initialDataLoadStatus: ExperienceResult;
	hasLoadedIssueDataFromCache: boolean;
	errorStatusCode?: number | undefined;
};

const initialExperienceTrackingState = {
	initialDataLoadStatus: EXPERIENCE_PENDING,
	hasLoadedIssueDataFromCache: false,
} as const;

const ERROR_STATUS_CODE_ALLOWLIST: number[] = [403, 401];

const isStatusCodeAnExperienceFailure = (
	statusCode?: number,
): boolean => // Void or 0 statusCode indicates connectivity problem and generated by client side
	// This indicates that browser has no connectivity
	// @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'number'.
	Boolean(statusCode) && !ERROR_STATUS_CODE_ALLOWLIST.includes(statusCode);

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (
	state: ExperienceTrackingState = initialExperienceTrackingState,
	action: Actions,
) => {
	switch (action.type) {
		case LOAD_ISSUES_SUCCESS: {
			let newStateChain = chain(state);
			if (action.payload.loadedIssuesResponse.source === LOCAL_STORAGE_SOURCE) {
				newStateChain = newStateChain.assoc('hasLoadedIssueDataFromCache', true);
			} else if (action.payload.source === LOAD_ISSUES_ACTION_SOURCE_PAGE) {
				// Do no set status to true if the payload is from localstorage, that doesn't count
				newStateChain = newStateChain.assoc('initialDataLoadStatus', EXPERIENCE_SUCCEEDED);
			}
			return newStateChain.value();
		}
		case LOAD_ISSUES_FAILURE: {
			if (action.payload.source === LOAD_ISSUES_ACTION_SOURCE_PAGE) {
				if (isStatusCodeAnExperienceFailure(action.payload.statusCode)) {
					return {
						...state,
						initialDataLoadStatus: EXPERIENCE_FAILED,
						errorStatusCode: action.payload.statusCode,
					};
				}
				return assoc(state, 'initialDataLoadStatus', EXPERIENCE_SUCCEEDED);
			}
			return state;
		}
		case LOAD_PAGE_DATA_FAILURE: {
			if (isStatusCodeAnExperienceFailure(action.payload.statusCode)) {
				return assoc(state, 'initialDataLoadStatus', EXPERIENCE_FAILED);
			}
			return state;
		}
		case RESET_STATE:
		case EXPERIENCE_TRACKING_RESET: {
			return initialExperienceTrackingState;
		}
		default: {
			const _exhaustiveCheck: never = action;
			return state;
		}
	}
};
